// src/lib/auth/return-to.ts
//
// Canonicalising validator for the post-auth `return_to` redirect.
//
// WHY A PARSER AND NOT PREFIX CHECKS (SEC-AUTH-06):
//   login.tsx previously accepted any value that started with a single '/'
//   and contained no colon before the first path separator. A browser does not
//   resolve URLs that way. `new URL('/\\evil.example', 'https://inverio.ai')`
//   normalises the backslash to '/', producing `https://evil.example/` — a
//   cross-origin open redirect that every one of those string checks passes.
//   Percent-encoded separators, embedded tabs/newlines inside a scheme, and
//   userinfo (`https://inverio.ai@evil.example`) all defeat the same style of
//   check for the same reason: they are canonicalisation bugs, and the only
//   thing that canonicalises correctly is the URL parser itself.
//
// THE RULE: parse against the trusted origin, require the parsed origin to be
// byte-equal to it, then navigate ONLY with the normalised local part. The
// input string is never handed to the router.

/** Upper bound on a plausible in-app path. Anything longer is not a route. */
const MAX_RETURN_TO_LENGTH = 2048;

/**
 * Characters that must never appear in a value we hand to the router.
 * Backslash is the canonicalisation hazard (browsers fold it to '/'); CR, LF
 * and NUL are request/response-splitting shapes; the C0 range covers the
 * tab/newline-inside-a-scheme tricks (`java\tscript:`) that the URL parser
 * strips before resolving — meaning a value that LOOKS inert can parse to a
 * live `javascript:` URL.
 */
// eslint-disable-next-line no-control-regex -- matching control chars is the point
const FORBIDDEN_CHARS = /[\\\x00-\x1f\x7f]/;

/**
 * Reduce an untrusted `return_to` value to a safe same-origin local path.
 *
 * @param raw          the untrusted value (typically an Expo Router search param,
 *                     which may be a string, an array, or undefined)
 * @param trustedOrigin the application's own origin, e.g. `window.location.origin`
 *                     on web or a synthetic origin on native
 * @returns `pathname + search + hash` when the value resolves to `trustedOrigin`,
 *          otherwise `'/'`. Never throws.
 */
export function sanitizeReturnTo(raw: unknown, trustedOrigin: string): string {
  if (typeof raw !== 'string') return '/';

  const candidate = raw.trim();
  if (candidate.length === 0 || candidate.length > MAX_RETURN_TO_LENGTH) return '/';

  // Reject before parsing: the parser is what turns these into a different
  // origin, so we must not give it the chance.
  if (FORBIDDEN_CHARS.test(candidate)) return '/';

  // A protocol-relative URL ('//host') resolves to a foreign origin and is the
  // classic open-redirect payload. It is rejected by the origin check below
  // too, but failing early keeps the intent legible.
  if (candidate.startsWith('//')) return '/';

  // Percent-encoded path separators. These survive `new URL()` un-decoded and
  // therefore stay same-origin, so the origin check below would PASS them —
  // but whether they stay encoded depends on who decodes next. Expo Router
  // decodes route segments, so `/%2f%2fevil.example` can re-emerge as
  // `//evil.example` one layer down. Reject the smuggle rather than reason
  // about every consumer. (`%20` and other non-separator escapes are fine and
  // must keep working — a query value may legitimately contain them.)
  if (/%(?:2f|5c|00)/i.test(candidate)) return '/';

  // Shape gate: the only two forms we ever generate are a rooted app path
  // ('/dealflow') and a fully-qualified same-origin URL. A bare relative value
  // ('dealflow', 'evil.example') is neither — and is ambiguous, since the
  // parser silently roots it, turning a hostname into a path. Requiring one of
  // the two known shapes means the string we validate is the string the router
  // receives, with no resolution step in between.
  if (!candidate.startsWith('/') && !/^https?:\/\//i.test(candidate)) return '/';

  let base: URL;
  try {
    base = new URL(trustedOrigin);
  } catch {
    // A caller handed us an origin we cannot parse. Without a trusted origin
    // there is nothing to compare against — refuse to redirect anywhere.
    return '/';
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate, base);
  } catch {
    return '/';
  }

  // The single load-bearing check. `URL.origin` is already normalised
  // (lowercased scheme + host, default port elided), so an exact string
  // comparison is the correct test — a lookalike host, a different port, a
  // downgraded scheme and a userinfo trick all produce a different origin.
  if (parsed.origin !== base.origin) return '/';

  // Opaque-origin schemes (`javascript:`, `data:`) serialise their origin as
  // the literal "null", which would never equal base.origin — but be explicit
  // so the guarantee does not rest on that detail alone.
  if (parsed.protocol !== base.protocol) return '/';

  // Malformed percent-encoding: `decodeURIComponent` throws on `%zz`. Such a
  // value is not a route we ever generate, and its browser-side interpretation
  // is inconsistent, so refuse it rather than guess.
  try {
    decodeURIComponent(parsed.pathname);
  } catch {
    return '/';
  }

  // Navigate with the PARSER's normalised local part, never the raw input.
  const local = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return local.startsWith('/') ? local : '/';
}

/**
 * The origin to validate `return_to` against.
 *
 * Web: the live origin, so preview/staging deployments and localhost all
 * validate against themselves. Native: there is no browser origin and Expo
 * Router paths are app-local, so a synthetic constant stands in — its only job
 * is to give the parser something to resolve relative paths against, and any
 * absolute URL then fails the origin comparison.
 */
export function getTrustedOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://inverio.ai';
}
